import { useCallback, useMemo, useState, useDeferredValue, useId } from "react";
import { CommonModalUI } from "../../ui";
import {
  PaginationState,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import CommonTable from "../../ui/Table/CommonTable";
import { PAGE_INDEX, PAGE_LIMIT } from "@/core/const";
import TableListingLoaders from "@/pages/layout/loaders/TableListingLoaders";
import ErrorFallback from "../../error-fallback/ErrorFallback";
import CommonNoRecordMsg from "../../ui/Card/CommonNoRecordMessage";
import CommonPaginationUI from "../../ui/Table/CommonPagination";
import { ColumnPinningState } from "@tanstack/react-table";
import {
  TGenericApiResponse,
  TQueryHook,
  TTransformFunction,
} from "@/core/types/generic-data-visualization";
import { TErrorData } from "@/core/types/error-fallback";

/**
 * Props for GenericDataVisualizationModal
 */
export type TGenericDataVisualizationModalProps<
  TParams extends Record<string, unknown>,
  TApiData,
  TTableData extends { id: string },
> = {
  /** Modal open/close state */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal size (default: "8xl") */
  size?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    | "8xl";
  /** API query hook */
  useQueryHook: TQueryHook<TParams, TApiData, TErrorData>;
  /** Query parameters */
  queryParams: TParams;
  /** Column definitions for the table */
  columns: ColumnDef<TTableData>[];
  /** Transform function to convert API data to table data */
  transformData: TTransformFunction<TApiData, TTableData>;
  /** Optional: Custom data extractor if API response structure differs */
  dataExtractor?: (response: TGenericApiResponse<TApiData>) => TApiData[];
  /** Optional: Custom count extractor if API response structure differs */
  countExtractor?: (response: TGenericApiResponse<TApiData>) => number;
  /** Optional: Query options */
  queryOptions?: {
    enabled?: boolean;
    retry?: boolean;
    refetchOnWindowFocus?: boolean;
    [key: string]: unknown;
  };
  /** Optional: Initial column pinning */
  initialColumnPinning?: ColumnPinningState;
  /** Optional: Custom table ID */
  tableId?: string;
  /** Optional: Custom pagination ID */
  paginationId?: string;
  /** Optional: Enable manual sorting (default: true) */
  manualSorting?: boolean;
  /** Optional: Custom empty state message */
  emptyMessage?: string;
  /** Optional: Custom loading component */
  loadingComponent?: React.ReactNode;
};

/**
 * Generic Data Visualization Modal Component
 *
 * A reusable modal component that displays data from any API query hook in a table format.
 * Supports pagination, sorting, and error handling.
 *
 * Enhanced with React 18/19 features:
 * - useDeferredValue for smooth data rendering
 * - startTransition for non-blocking pagination/sorting
 * - useId for SSR-safe IDs
 *
 * @example
 * ```tsx
 * <GenericDataVisualizationModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Evaluation List"
 *   useQueryHook={useGetEvaluationListQuery}
 *   queryParams={{ page: 1, page_size: 10 }}
 *   columns={columns}
 *   transformData={transformEvaluationData}
 * />
 * ```
 */
function GenericDataVisualizationModal<
  TParams extends Record<string, unknown>,
  TApiData,
  TTableData extends { id: string },
>({
  isOpen,
  onClose,
  title,
  size = "8xl",
  useQueryHook,
  queryParams,
  columns,
  transformData,
  dataExtractor,
  countExtractor,
  queryOptions,
  initialColumnPinning,
  tableId,
  paginationId,
  manualSorting = true,
  emptyMessage,
  loadingComponent,
}: TGenericDataVisualizationModalProps<TParams, TApiData, TTableData>) {
  // React 18: useId for SSR-safe unique IDs
  const defaultTableId = useId();
  const defaultPaginationId = useId();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: PAGE_INDEX,
    pageSize: PAGE_LIMIT,
  });

  // Memoize pagination object
  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  // Merge query params with pagination
  const mergedQueryParams = useMemo<TParams>(() => {
    const sortingParams =
      sorting && sorting.length > 0
        ? {
            sort_by: sorting[0].id,
            sort_order: sorting[0].desc ? "desc" : "asc",
          }
        : {};

    return {
      ...queryParams,
      ...sortingParams,
      page: pageIndex + 1,
      page_size: pageSize,
    };
  }, [queryParams, sorting, pageIndex, pageSize]);

  // Execute query hook
  const { data, isError, error, isLoading } = useQueryHook(
    mergedQueryParams,
    queryOptions,
  );

  // Extract data from response (with custom extractor support)
  const extractedData = useMemo((): TApiData[] => {
    if (!data) return [];

    if (dataExtractor) {
      return dataExtractor(data);
    }

    // Default: assume standard structure
    // Handle React Query response structure: data.data.results
    // The data from React Query is the API response payload directly
    const apiResponse = data as {
      data?: { results?: TApiData[]; [key: string]: unknown };
      [key: string]: unknown;
    };
    const results = apiResponse?.data?.results;
    return Array.isArray(results) ? results : [];
  }, [data, dataExtractor]);

  // Transform API data to table data
  const transformedData = useMemo(() => {
    if (extractedData.length > 0) {
      return transformData(extractedData);
    }
    return [];
  }, [extractedData, transformData]);

  // React 18: useDeferredValue for smooth rendering of large datasets
  const deferredRows = useDeferredValue(transformedData);

  // Extract total count (with custom extractor support)
  const totalListCount = useMemo((): number => {
    if (!data) return 0;

    if (countExtractor) {
      return countExtractor(data);
    }

    // Default: assume standard structure
    // Handle React Query response structure: data.data.count
    // The data from React Query is the API response payload directly
    const apiResponse = data as {
      data?: { count?: number; [key: string]: unknown };
      [key: string]: unknown;
    };
    const count = apiResponse?.data?.count;
    return typeof count === "number" ? count : 0;
  }, [data, countExtractor]);

  // Calculate pagination values
  const pageCount = Math.ceil(totalListCount / pageSize);
  const disablePrev = pageIndex === 0;
  const disableNext = pageIndex >= pageCount - 1;

  // Optimized page change handler with startTransition
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPagination({ pageIndex: newPage, pageSize });
    },
    [pageSize],
  );

  // Optimized rows per page change handler
  const handleRowsPerPageChange = useCallback((newPageSize: number) => {
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  }, []);

  // Optimized sorting handler
  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      setSorting(updater);
    },
    [],
  );

  if (!isOpen) return null;

  return (
    <CommonModalUI
      open={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      type="default"
      id={`generic-data-visualization-modal-${defaultTableId}`}
      classNameModal="generic-modal-body"
    >
      {/* Loading state */}
      {isLoading && (loadingComponent ?? <TableListingLoaders />)}

      {/* Data display */}
      {!isLoading && deferredRows.length > 0 ? (
        <ErrorFallback isError={isError} error={error ?? undefined}>
          <div className="bg-background w-full overflow-x-auto">
            <CommonTable
              columns={columns}
              rows={deferredRows}
              totalRecordCount={totalListCount}
              setSorting={handleSortingChange}
              sorting={sorting}
              pagination={pagination}
              setPagination={setPagination}
              manualSorting={manualSorting}
              initialColumnPinning={initialColumnPinning}
              id={tableId ?? `generic-table-${defaultTableId}`}
            />
          </div>
          <CommonPaginationUI
            totalRecordCount={totalListCount}
            page={pageIndex}
            pageCount={pageCount}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            disablePrev={disablePrev}
            disableNext={disableNext}
            id={paginationId ?? `generic-pagination-${defaultPaginationId}`}
          />
        </ErrorFallback>
      ) : (
        !isLoading &&
        transformedData?.length == 0 && (
          <CommonNoRecordMsg className="text-center" msg={emptyMessage} />
        )
      )}
    </CommonModalUI>
  );
}

export default GenericDataVisualizationModal;
